package org.zest.insan_360_website.data.page_states.dashboard

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.url.URL
import org.zest.insan_360_website.components.widgets.ToastState
import org.zest.insan_360_website.components.widgets.ToastType
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.AuthState
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState.isVerifying
import org.zest.insan_360_website.model.*
import org.zest.insan_360_website.utils.NetworkUtils
import org.zest.insan_360_website.utils.processResponse
import kotlin.js.Promise
import kotlin.js.json

object ReportDashPageState {
    var testUsers by mutableStateOf<TestUsersPayload?>(null)
    var selectedUser by mutableStateOf<SelectedUser?>(null)
    var templates by mutableStateOf<List<String>>(emptyList())
    var selectedTemplate by mutableStateOf<String?>(null)
    var submissionsHistory by mutableStateOf<SubmissionsHistoryPayload?>(null)

    var isDownloading by mutableStateOf(false)
    var isLoading by mutableStateOf(true)
    var isLoadingHistory by mutableStateOf(true)
    var isFetching by mutableStateOf(false)
    var isVerifying by mutableStateOf(false)
    var needToFetch by mutableStateOf(true)
    var isRequestingReport by mutableStateOf(false)

    enum class TestSubmissionFilter {
        ALL, PENDING, VERIFIED, REJECTED
    }

    enum class TestSubmissionsTab {
        LATEST, PAST
    }

    var searchQuery by mutableStateOf("")
    var statusFilter by mutableStateOf(TestSubmissionFilter.ALL)
    var activeTab by mutableStateOf(TestSubmissionsTab.LATEST)

    fun reset() {
        testUsers = null
        selectedUser = null
        templates = emptyList()
        selectedTemplate = null
        submissionsHistory = null
        isDownloading = false
        isLoading = true
        isLoadingHistory = true
        isFetching = false
        isVerifying = false
        needToFetch = true
        isRequestingReport = false
        searchQuery = ""
        statusFilter = TestSubmissionFilter.ALL
        activeTab = TestSubmissionsTab.LATEST
    }

    fun getFilteredSubmissions(): List<TestUser> {
        val users = testUsers?.users ?: return emptyList()
        val sortedUsers = users.sortedByDescending { it.submittedAt }
        val grouped = sortedUsers.groupBy { it.email }
        
        val targetList = if (activeTab == TestSubmissionsTab.LATEST) {
            grouped.map { it.value.first() }
        } else {
            grouped.flatMap { it.value.drop(1) }
        }

        return targetList.filter { user ->
            val matchesSearch = if (searchQuery.isBlank()) true else {
                val q = searchQuery.lowercase()
                user.name.lowercase().contains(q) || 
                user.email.lowercase().contains(q) || 
                (user.orgName?.lowercase()?.contains(q) == true)
            }

            val matchesStatus = when (statusFilter) {
                TestSubmissionFilter.ALL -> true
                TestSubmissionFilter.PENDING -> user.status == TestSubmissionStatus.PENDING
                TestSubmissionFilter.VERIFIED -> user.status == TestSubmissionStatus.VERIFIED
                TestSubmissionFilter.REJECTED -> user.status == TestSubmissionStatus.REJECTED
            }

            matchesSearch && matchesStatus
        }
    }

    fun getFilteredSubmissionsHistory(): List<SubmissionHistory> {
        val historyList = submissionsHistory?.submissions ?: return emptyList()
        val sortedHistory = historyList.sortedByDescending { it.submittedAt }
        
        return sortedHistory.filter { submission ->
            val matchesSearch = if (searchQuery.isBlank()) true else {
                val q = searchQuery.lowercase()
                submission.testId.lowercase().contains(q) || 
                submission.creditId.lowercase().contains(q) || 
                (submission.orgName?.lowercase()?.contains(q) == true)
            }

            val matchesStatus = when (statusFilter) {
                TestSubmissionFilter.ALL -> true
                TestSubmissionFilter.PENDING -> submission.status == TestSubmissionStatus.PENDING
                TestSubmissionFilter.VERIFIED -> submission.status == TestSubmissionStatus.VERIFIED
                TestSubmissionFilter.REJECTED -> submission.status == TestSubmissionStatus.REJECTED
            }

            matchesSearch && matchesStatus
        }
    }

    suspend fun fetchSubmissionHistory() {
        if (AuthState.token == null) return

        isLoadingHistory = true

        try {
            val response = NetworkUtils.getSecureApi("/api/test/history")
                ?: throw Exception("Session expired. Please log in again")
            submissionsHistory = processResponse<SubmissionsHistoryPayload>(response, fallbackErrorLog = "Error loading submission history")
        } catch (e: Exception) {
            console.log("Network error fetching submission history: ${e.message}")
        } finally {
            isLoadingHistory = false
        }
    }

    fun downloadVerifiedReport(targetTestId: String, toast: ToastState, scope: CoroutineScope) {
        val email = AuthState.email
        val token = AuthState.token
        if (email == null) {
            toast.show("User email not found. Cannot download report.", ToastType.ERROR)
            return
        }
        if (token == null) {
            toast.show("User token not found. Cannot download report.", ToastType.ERROR)
            return
        }

        isDownloading = true

        scope.launch {
            toast.show(LanguageSettings.UserDashboardPage.download_report.localized + "...")
            try {
                val request = DownloadReportRequest(
                    testId = targetTestId,
                    email = email,
                )
                val response = NetworkUtils.postSecureApi("/api/test/report/download", request)
                    ?: throw Exception("Session expired. Please log in again.")
                if (response.ok) {
                    val blob = response.blob().await()
                    val url = URL.createObjectURL(blob)
                    val link = document.createElement("a") as HTMLAnchorElement

                    link.href = url
                    link.download = "DISC_Report_$targetTestId.pdf"
                    document.body?.appendChild(link)
                    link.click()
                    document.body?.removeChild(link)
                    URL.revokeObjectURL(url)
                } else {
                    val errorText = response.text().await()
                    val result = Json.decodeFromString<GenericResponse>(errorText)

                    toast.show(result.toString(), ToastType.ERROR)
                    console.error("Backend Error: $result")
                }
            } catch (e: Exception) {
                toast.show("Network error fetching report status: ${e.message}")
                console.log("Error downloading report: ${e.message}")
            } finally {
                isDownloading = false
            }
        }
    }

    fun requestTestReport(targetTestId: String, toast: ToastState, scope: CoroutineScope) {
        val email = AuthState.email
        val token = AuthState.token
        if (email == null || token == null) {
            toast.show("Session error. Please log in again.", ToastType.ERROR)
            return
        }

        isRequestingReport = true
        scope.launch {
            try {
                val request = RequestReportRequest(targetTestId)
                val response = NetworkUtils.postSecureApi("/api/test/report/request", request)
                    ?: throw Exception("Session expired. Please log in again.")
                if (response.ok) {
                    toast.show("Report request sent successfully!", ToastType.SUCCESS)
                    fetchSubmissionHistory() // Refresh the list
                } else {
                    val errorText = response.text().await()
                    val result = Json.decodeFromString<GenericResponse>(errorText)
                    toast.show("Error: ${result.debugMessage ?: "Failed to request report"}", ToastType.ERROR)
                }
            } catch (e: Exception) {
                toast.show("Network error requesting report: ${e.message}")
                console.error("Error requesting report: ${e.message}")
            } finally {
                isRequestingReport = false
            }
        }
    }

    fun fetchTestUsers(scope: CoroutineScope) {
        val email = AuthState.email ?: return
        val role = AuthState.role
        if (AuthState.token == null) return

        if (role == UserRole.STUDENT) {
            isLoading = false
            return
        }
        isLoading = true

        scope.launch {
            try {
                val response =
                    if (role == UserRole.SUPER_ADMIN) {
                        val request = SuperAdminRequest<Unit>(
                            email = email,
                            role = role
                        )
                        NetworkUtils.postSecureApi("/api/super-admin/dashboard/test-users", request)
                            ?: throw Exception("Session expired. Please log in again.")
                    } else {
                        val request = AdminRequest(
                            email = email,
                            role = role,
                            request = AdminStudentsListRequest(
                                email = email
                            )
                        )
                        NetworkUtils.postSecureApi("/api/admin/dashboard/test-users", request)
                    } ?: throw Exception("Session expired. Please log in again.")

                testUsers =
                    processResponse<TestUsersPayload>(response, fallbackErrorLog = "Error fetching students list")

            } catch (e: Exception) {
                console.error("Network error fetching user list: ${e.message}")
                testUsers = null
            } finally {
                isLoading = false
            }
        }
    }

    fun fetchTestUserInfo(userToFetch: TestUser, toast: ToastState, scope: CoroutineScope) {
        val email = AuthState.email
        val role = AuthState.role
        val token = AuthState.token
        var fetchedUser: UserTestInfoPayload?

        if (role == UserRole.STUDENT) {
            toast.show("Unauthorized access. Only Admins can fetch user info.", ToastType.ERROR)
            return
        }
        if (token == null) {
            toast.show("User token not found. Cannot fetch user info. Check your session", ToastType.ERROR)
            return
        }
        if (email == null) {
            toast.show("User email not found. Cannot fetch user info. Check your session", ToastType.ERROR)
            return
        }
        isFetching = true

        scope.launch {
            try {
                val response =
                    if (role == UserRole.SUPER_ADMIN) {
                        val request = SuperAdminRequest(
                            email = email,
                            role = role,
                            request = UserTestDataRequest(
                                testId = userToFetch.testId
                            )
                        )
                        NetworkUtils.postSecureApi("/api/super-admin/dashboard/test-user-info", request)
                    } else {
                        val request = AdminRequest(
                            email = email,
                            role = role,
                            request = OrgUserTestDataRequest(
                                email = email,
                                testId = userToFetch.testId,
                            )
                        )
                        NetworkUtils.postSecureApi("/api/admin/dashboard/test-user-info", request)
                    } ?: throw Exception("Session expired. Please log in again.")

                fetchedUser = processResponse(response, toast, "Error fetching user info")
                fetchedUser?.let {
                    selectedUser = SelectedUser(
                        id = userToFetch.id,
                        name = userToFetch.name,
                        email = userToFetch.email,
                        testId = userToFetch.testId,
                        phone = userToFetch.phone,
                        ic = it.nationalId,
                        answers = it.answers,
                        submittedAt = userToFetch.submittedAt
                    )
                } ?: run {
                    selectedUser = null
                }

            } catch (e: Exception) {
                toast.show("Network error fetching user info: ${e.message}")
                console.error("Network error fetching user info: ${e.message}")
                selectedUser = null
            } finally {
                isFetching = false
            }
        }
    }

    fun fetchReportTemplates(scope: CoroutineScope) {
        val email = AuthState.email ?: return
        val role = AuthState.role
        if (AuthState.token == null) return

        if (role == UserRole.STUDENT) {
            return
        }

        scope.launch {
            try {
                val response =
                    if (role == UserRole.SUPER_ADMIN) {
                        val request = SuperAdminRequest<Unit>(
                            email = email,
                            role = role
                        )
                        NetworkUtils.postSecureApi("/api/super-admin/templates/list", request)
                    } else {
                        val request = AdminRequest<Unit>(
                            email = email,
                            role = role
                        )
                        NetworkUtils.postSecureApi("/api/admin/templates/list", request)
                    } ?: throw Exception("Session expired. Please log in again.")

                templates =
                    processResponse<List<String>>(response, fallbackErrorLog = "Error fetching report templates")
                        ?: emptyList()

            } catch (e: Exception) {
                console.error("Error fetching report templates: ${e.message}")
                templates = emptyList()
            }
        }
    }
}

@JsName("html2canvas")
external fun html2canvas(element: HTMLElement, options: dynamic = definedExternally): Promise<dynamic>

fun submitGraphForVerification(
    verifierEmail: String,
    elementId: String,
    templateName: String,
    submissionId: String,
    userName: String,
    userNric: String,
    scope: CoroutineScope,
    toast: ToastState,
    onSuccess: () -> Unit,
) {
    if (AuthState.token == null) return
    val role = AuthState.role
    if (role == UserRole.STUDENT) {
        return
    }
    val element = document.getElementById(elementId) as? HTMLElement ?: return

    isVerifying = true

    scope.launch {
        try {
            val renderOptions = json(
                "scale" to 1.5,
                "useCORS" to true,
                "logging" to false
            )
            val canvas: dynamic = html2canvas(element, renderOptions).await()
            val imgData: String = canvas.toDataURL("image/jpeg", 0.8)
            val response =
                if (role == UserRole.SUPER_ADMIN) {
                    val request = SuperAdminRequest(
                        email = verifierEmail,
                        role = role,
                        request = GenerateReportRequest(
                            verifierEmail = verifierEmail,
                            submissionId = submissionId,
                            templateName = templateName,
                            userName = userName,
                            userNric = userNric,
                            graphImageBase64 = imgData
                        )
                    )
                    NetworkUtils.postSecureApi("/api/super-admin/reports/generate", request)
                } else {
                    val request = AdminRequest(
                        email = verifierEmail,
                        role = role,
                        request = GenerateReportRequest(
                            verifierEmail = verifierEmail,
                            submissionId = submissionId,
                            templateName = templateName,
                            userName = userName,
                            userNric = userNric,
                            graphImageBase64 = imgData
                        )
                    )
                    NetworkUtils.postSecureApi("/api/admin/reports/generate", request)
                } ?: throw Exception("Session Expired. Please log in again")

            processResponse<Unit>(response, toast, "Failed to generate test report.") {
                onSuccess()
            }

        } catch (e: Exception) {
            toast.show("Failed to generate report on server: ${e.message}")
            console.error("Error generating report: ${e.message}")
        } finally {
            isVerifying = false
        }
    }
}

fun rejectSubmission(
    verifierEmail: String,
    submissionId: String,
    scope: CoroutineScope,
    toast: ToastState,
    onSuccess: () -> Unit,
) {
    if (AuthState.token == null) return
    val role = AuthState.role
    if (role == UserRole.STUDENT) {
        return
    }

    isVerifying = true

    scope.launch {
        try {
            val response =
                if (role == UserRole.SUPER_ADMIN) {
                    val request = SuperAdminRequest(
                        email = verifierEmail,
                        role = role,
                        request = RejectTestRequest(
                            submissionId = submissionId
                        )
                    )
                    NetworkUtils.postSecureApi("/api/super-admin/reports/reject", request)
                } else {
                    val request = AdminRequest(
                        email = verifierEmail,
                        role = role,
                        request = RejectTestRequest(
                            submissionId = submissionId
                        )
                    )
                    NetworkUtils.postSecureApi("/api/admin/reports/reject", request)
                } ?: throw Exception("Session Expired. Please log in again")

            processResponse<Unit>(response, toast, "Failed to reject test submission.") {
                onSuccess()
            }
        } catch (e: Exception) {
            toast.show("Failed to reject report on server: ${e.message}")
            console.error("Error rejecting report: ${e.message}")
        } finally {
            isVerifying = false
        }
    }
}